// Clusters and similar entities (PLAN §12.2 "By cluster / similarity", §9 Bubble / clusters; task 34).
// The cluster job finds communities in the relation graph (Louvain, weighted by link strength). Each cluster keeps
// its id, color and label across recomputes by matching member overlap; the 12 largest get a palette color and the
// rest fall back to grey. Everything here is pure, shared by the job, the API and the screens.

import type { EntitySummary, ReviewStatus } from './claims';
import { fnv1a } from './colors';
import { ENTITY_SLOTS } from './design/tokens';
import type { JobProgress, StageDef } from './progress';
import type { Sensitivity } from './uploads';

/** Where a cluster's label came from: the AI, a reviewer, or the fallback ("Around <most central member>"). */
export const CLUSTER_LABEL_SOURCES = ['ai', 'manual', 'auto'] as const;
export type ClusterLabelSource = (typeof CLUSTER_LABEL_SOURCES)[number];

export const CLUSTER_RUN_STATES = ['queued', 'running', 'done', 'failed'] as const;
export type ClusterRunState = (typeof CLUSTER_RUN_STATES)[number];

/** Nightly cron, after a large review, or someone pressed Recompute. */
export const CLUSTER_TRIGGERS = ['nightly', 'review', 'manual'] as const;
export type ClusterTrigger = (typeof CLUSTER_TRIGGERS)[number];

/** Communities smaller than this are not clusters: a pair says little, and would crowd out the palette. */
export const CLUSTER_MIN_SIZE = 3;
/** A new community takes over an old cluster (id, color, label) when their members overlap at least this much. */
export const CLUSTER_MATCH_MIN_OVERLAP = 0.2;
/** The label is kept when the members overlap at least this much; below it, the cluster is named again. */
export const CLUSTER_KEEP_LABEL_OVERLAP = 0.5;
/** Betweenness is computed for clusters up to this size (it is O(V·E)); larger ones are sized by degree only. */
export const CLUSTER_BETWEENNESS_MAX = 400;
/** Clusters named by the AI per run (largest first); the rest keep the fallback label until the next run. */
export const CLUSTER_LABELS_PER_RUN = 25;
/** A review that committed at least this many claims queues a recompute ("after large reviews"). */
export const CLUSTER_REVIEW_THRESHOLD = 25;
/** A queued or running recompute older than this is considered dead, and a new one may start. */
export const CLUSTER_RUN_STALE_MS = 15 * 60_000;
export const CLUSTER_LABEL_MAX = 80;

/** Link strength for community detection: reviewed links count more than unreviewed or disputed ones. */
export const LINK_WEIGHTS: Record<ReviewStatus, number> = { verified: 1, unreviewed: 0.5, disputed: 0.25, rejected: 0 };

/** What travels with an entity so every view can color it by cluster. */
export interface ClusterRef {
  id: string;
  label: string | null;
  colorSlot: number | null;
}

export interface ClusterSummary extends ClusterRef {
  labelSource: ClusterLabelSource;
  size: number;
  sensitivity: Sensitivity;
  computedAt: number;
}

export interface ClusterRunStatus {
  id: string;
  state: ClusterRunState;
  trigger: ClusterTrigger;
  startedAt: number | null;
  finishedAt: number | null;
  clusters: number | null;
  entities: number | null;
  note: string | null;
  createdAt: number;
}

export type ClusterMember = EntitySummary & { degree: number; betweenness: number | null };

/** The bubble view: the largest clusters with their most central members. */
export interface ClusterBubbles {
  clusters: (ClusterSummary & { members: ClusterMember[]; truncated: boolean })[];
  /** Clusters not shown (beyond the limit). */
  more: number;
  lastRun: ClusterRunStatus | null;
  /** A run is queued or running. */
  running: ClusterRunStatus | null;
}

/** Queue message for the cluster job (pipeline Worker). */
export interface ClusterRunMessage {
  kind: 'cluster_run';
  runId: string;
  userId: string | null;
}
export const isClusterRunMessage = (m: unknown): m is ClusterRunMessage =>
  !!m && typeof m === 'object' && (m as ClusterRunMessage).kind === 'cluster_run' && typeof (m as ClusterRunMessage).runId === 'string';

/** "Cluster: Al-X Holding network" — the "why this color?" of cluster mode. */
export function clusterWhy(c: ClusterRef | null | undefined): string {
  if (!c) return 'Color by cluster: not in a cluster';
  const name = c.label ?? 'unnamed cluster';
  return c.colorSlot ? `Cluster: ${name}` : `Cluster: ${name} (grey: beyond the ${ENTITY_SLOTS} largest)`;
}

export function checkClusterLabel(v: unknown): { ok: true; label: string } | { ok: false; error: string } {
  const label = typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '';
  if (!label) return { ok: false, error: 'Enter a name for the cluster.' };
  if (label.length > CLUSTER_LABEL_MAX) return { ok: false, error: `Keep the name under ${CLUSTER_LABEL_MAX} characters.` };
  return { ok: true, label };
}

// ─── Stable identity across recomputes ───────────────────────────────────────

export interface PreviousCluster {
  id: string;
  members: readonly string[];
  colorSlot: number | null;
  label: string | null;
  labelSource: ClusterLabelSource;
}

export interface AssignedCluster {
  /** The old cluster this community continues, or null for a new one. */
  previousId: string | null;
  members: string[];
  colorSlot: number | null;
  /** Keep the old label (members mostly the same, or a reviewer named it). */
  keepLabel: boolean;
  /** Jaccard overlap with the old cluster (0 when new). */
  overlap: number;
}

/**
 * Matches new communities to the previous clusters by member overlap (Jaccard, best pairs first), so a cluster keeps
 * its id, color and label while its members mostly stay together. The `ENTITY_SLOTS` largest communities get a color:
 * a continuing cluster keeps its old one, new ones take the free slots in order; the rest are grey (null).
 * Deterministic: ties break by size, then by the smallest member id.
 */
export function assignClusters(previous: readonly PreviousCluster[], communities: readonly (readonly string[])[]): AssignedCluster[] {
  const next = communities
    .map(m => [...new Set(m)].sort())
    .filter(m => m.length >= CLUSTER_MIN_SIZE)
    .sort((a, b) => b.length - a.length || (a[0] ?? '').localeCompare(b[0] ?? ''));
  const prevOf = new Map<string, number>();
  previous.forEach((p, j) => {
    for (const id of p.members) prevOf.set(id, j);
  });
  const pairs: { i: number; j: number; jaccard: number; inter: number }[] = [];
  next.forEach((members, i) => {
    const inter = new Map<number, number>();
    for (const id of members) {
      const j = prevOf.get(id);
      if (j !== undefined) inter.set(j, (inter.get(j) ?? 0) + 1);
    }
    for (const [j, n] of inter) {
      const size = previous[j]?.members.length ?? 0;
      pairs.push({ i, j, inter: n, jaccard: n / (members.length + size - n) });
    }
  });
  pairs.sort((a, b) => b.jaccard - a.jaccard || b.inter - a.inter || a.i - b.i || a.j - b.j);
  const matchOf = new Map<number, { j: number; jaccard: number }>();
  const usedPrev = new Set<number>();
  for (const p of pairs) {
    if (p.jaccard < CLUSTER_MATCH_MIN_OVERLAP) break;
    if (matchOf.has(p.i) || usedPrev.has(p.j)) continue;
    matchOf.set(p.i, { j: p.j, jaccard: p.jaccard });
    usedPrev.add(p.j);
  }
  const out: AssignedCluster[] = next.map((members, i) => {
    const m = matchOf.get(i);
    const prev = m ? previous[m.j] : undefined;
    return {
      previousId: prev?.id ?? null,
      members,
      colorSlot: null,
      keepLabel: !!prev?.label && (prev.labelSource === 'manual' || (m?.jaccard ?? 0) >= CLUSTER_KEEP_LABEL_OVERLAP),
      overlap: m ? Math.round(m.jaccard * 1000) / 1000 : 0,
    };
  });
  const colored = out.slice(0, ENTITY_SLOTS);
  const taken = new Set<number>();
  for (const c of colored) {
    const old = c.previousId ? previous.find(p => p.id === c.previousId)?.colorSlot : null;
    if (old && old >= 1 && old <= ENTITY_SLOTS && !taken.has(old)) {
      c.colorSlot = old;
      taken.add(old);
    }
  }
  let slot = 1;
  for (const c of colored) {
    if (c.colorSlot) continue;
    while (taken.has(slot)) slot++;
    c.colorSlot = slot;
    taken.add(slot);
  }
  return out;
}

/** A short signature of a member set, to tell whether a label was written for (roughly) these members. */
export function membersSig(members: readonly string[]): string {
  return `${members.length}:${fnv1a([...members].sort().join(','))}`;
}

/** Jaccard overlap of two member lists. */
export function overlapOf(a: readonly string[], b: readonly string[]): number {
  const sa = new Set(a);
  let inter = 0;
  for (const x of new Set(b)) if (sa.has(x)) inter++;
  const union = sa.size + new Set(b).size - inter;
  return union ? inter / union : 0;
}

/** The fallback label, until (or when) the AI can't name a cluster: "Around <most central member>". */
export const fallbackClusterLabel = (topName: string | null | undefined) =>
  topName ? `Around ${topName}`.slice(0, CLUSTER_LABEL_MAX) : null;

// ─── Similar entities ────────────────────────────────────────────────────────

export interface SimilarEntity {
  entity: EntitySummary;
  /** 0–1. */
  score: number;
  /** Entities both are linked to (up to 5 named), and how many in all. */
  sharedNeighbors: { id: string; displayName: string }[];
  sharedNeighborCount: number;
  sameCluster: boolean;
  /** Name embedding similarity (0–1), where vectors are available. */
  nameScore: number | null;
  /** Tag labels both carry. */
  sharedTags: string[];
}

/** How much each signal counts in the similarity score. */
export const SIMILARITY_WEIGHTS = { neighbors: 0.55, cluster: 0.15, name: 0.2, tags: 0.1 } as const;
export const SIMILAR_MIN_SCORE = 0.12;
export const SIMILAR_LIMIT = 8;

/** One score from the signals; neighbor and tag overlap are Jaccard, the name score is cosine similarity. */
export function similarityScore(s: {
  neighborJaccard: number;
  sameCluster: boolean;
  nameScore: number | null;
  tagJaccard: number;
}): number {
  const w = SIMILARITY_WEIGHTS;
  const v = w.neighbors * s.neighborJaccard + (s.sameCluster ? w.cluster : 0) + w.name * (s.nameScore ?? 0) + w.tags * s.tagJaccard;
  return Math.round(Math.min(1, v) * 1000) / 1000;
}

/** "3 shared links (Meridian Forum, Harbor Aid…) · same cluster · similar name". */
export function similarityReasons(s: SimilarEntity): string[] {
  const out: string[] = [];
  if (s.sharedNeighborCount) out.push(`${s.sharedNeighborCount} shared ${s.sharedNeighborCount === 1 ? 'link' : 'links'}`);
  if (s.sameCluster) out.push('same cluster');
  if (s.nameScore !== null && s.nameScore >= 0.75) out.push('similar name');
  if (s.sharedTags.length) out.push(`tagged ${s.sharedTags.slice(0, 3).join(', ')}`);
  return out;
}

// ─── Progress card ───────────────────────────────────────────────────────────

export const CLUSTER_STAGES: StageDef[] = [
  { key: 'reading', label: 'Reading the links', stallAfterMs: 60_000 },
  { key: 'communities', label: 'Finding clusters', stallAfterMs: 120_000 },
  { key: 'labels', label: 'Naming clusters', stallAfterMs: 180_000 },
  { key: 'saving', label: 'Saving', stallAfterMs: 60_000 },
  { key: 'done', label: 'Done', stallAfterMs: Number.POSITIVE_INFINITY },
];

export const clusterJobId = (runId: string) => `cluster-${runId}`;

export function clusterJob(run: { id: string; startedAt: number }, stageIndex: number, patch: Partial<JobProgress> = {}): JobProgress {
  return {
    id: clusterJobId(run.id),
    kind: 'cluster',
    title: 'Recompute clusters',
    stages: CLUSTER_STAGES,
    stageIndex,
    percent: Math.min(99, Math.round((stageIndex / (CLUSTER_STAGES.length - 1)) * 100)),
    status: 'running',
    startedAt: run.startedAt,
    updatedAt: Date.now(),
    link: '/clusters',
    ...patch,
  };
}
